import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, BarChart3, FileText, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAvaliacaoGUTFormulario, calcularScoreGUT, getGUTPrioridade, GUT_LABELS } from '@/hooks/useAvaliacaoGUTFormulario';
import { useDiagnosticoA3Formulario } from '@/hooks/useDiagnosticoA3Formulario';
import { GUTScoreDisplay } from '../components/GUTScoreDisplay';
import { CincoPorquesWizard } from '../components/CincoPorquesWizard';
import { useAuth } from '@/hooks/useAuth';

interface TabAnaliseProps {
  planoAcaoId: string;
  readOnly?: boolean;
}

const getValueColor = (value: number) => {
  switch (value) {
    case 1: return 'bg-green-500 hover:bg-green-600 text-white';
    case 2: return 'bg-lime-500 hover:bg-lime-600 text-white';
    case 3: return 'bg-yellow-500 hover:bg-yellow-600 text-white';
    case 4: return 'bg-orange-500 hover:bg-orange-600 text-white';
    case 5: return 'bg-red-500 hover:bg-red-600 text-white';
    default: return 'bg-muted hover:bg-muted/80';
  }
};

const getSelectedColor = (value: number) => {
  switch (value) {
    case 1: return 'ring-2 ring-green-500 ring-offset-2';
    case 2: return 'ring-2 ring-lime-500 ring-offset-2';
    case 3: return 'ring-2 ring-yellow-500 ring-offset-2';
    case 4: return 'ring-2 ring-orange-500 ring-offset-2';
    case 5: return 'ring-2 ring-red-500 ring-offset-2';
    default: return '';
  }
};

const AREAS_DISPONIVEIS = [
  'TI', 'RH', 'Financeiro', 'Operações', 'Comercial', 
  'Marketing', 'Jurídico', 'Qualidade', 'Logística', 'Produção'
];

export function TabAnalise({ planoAcaoId, readOnly = false }: TabAnaliseProps) {
  const { user } = useAuth();
  const [subTab, setSubTab] = useState('gut');
  
  // GUT State
  const { avaliacao, isLoading: isLoadingGUT, saveAvaliacao, isSaving: isSavingGUT } = useAvaliacaoGUTFormulario(planoAcaoId);
  const [gravidade, setGravidade] = useState<number>(1);
  const [urgencia, setUrgencia] = useState<number>(1);
  const [tendencia, setTendencia] = useState<number>(1);
  const [gravidadeJustificativa, setGravidadeJustificativa] = useState('');
  const [urgenciaJustificativa, setUrgenciaJustificativa] = useState('');
  const [tendenciaJustificativa, setTendenciaJustificativa] = useState('');

  // A3 State
  const { diagnostico, isLoading: isLoadingA3, saveDiagnostico, isSaving: isSavingA3, isDiagnosticoCompleto } = useDiagnosticoA3Formulario(planoAcaoId);
  const [situacaoAtual, setSituacaoAtual] = useState('');
  const [impactoObservado, setImpactoObservado] = useState('');
  const [causaRaizIdentificada, setCausaRaizIdentificada] = useState('');
  const [escopoPlano, setEscopoPlano] = useState('');
  const [areasEnvolvidas, setAreasEnvolvidas] = useState<string[]>([]);
  const [porques, setPorques] = useState<{
    porque_1?: string;
    porque_2?: string;
    porque_3?: string;
    porque_4?: string;
    porque_5?: string;
  }>({});

  // Load GUT data
  useEffect(() => {
    if (avaliacao) {
      setGravidade(avaliacao.gravidade);
      setUrgencia(avaliacao.urgencia);
      setTendencia(avaliacao.tendencia);
      setGravidadeJustificativa(avaliacao.gravidade_justificativa || '');
      setUrgenciaJustificativa(avaliacao.urgencia_justificativa || '');
      setTendenciaJustificativa(avaliacao.tendencia_justificativa || '');
    }
  }, [avaliacao]);

  // Load A3 data
  useEffect(() => {
    if (diagnostico) {
      setSituacaoAtual(diagnostico.situacao_atual || '');
      setImpactoObservado(diagnostico.impacto_observado || '');
      setCausaRaizIdentificada(diagnostico.causa_raiz_identificada || '');
      setEscopoPlano(diagnostico.escopo_plano || '');
      setAreasEnvolvidas(diagnostico.areas_envolvidas || []);
      setPorques({
        porque_1: diagnostico.porque_1 || '',
        porque_2: diagnostico.porque_2 || '',
        porque_3: diagnostico.porque_3 || '',
        porque_4: diagnostico.porque_4 || '',
        porque_5: diagnostico.porque_5 || '',
      });
    }
  }, [diagnostico]);

  const handleSaveGUT = () => {
    saveAvaliacao({
      id: avaliacao?.id,
      formulario_plano_acao_id: planoAcaoId,
      gravidade,
      urgencia,
      tendencia,
      gravidade_justificativa: gravidadeJustificativa || null,
      urgencia_justificativa: urgenciaJustificativa || null,
      tendencia_justificativa: tendenciaJustificativa || null,
      avaliado_por: user?.id || null,
    });
  };

  const handleSaveA3 = () => {
    saveDiagnostico({
      id: diagnostico?.id,
      formulario_plano_acao_id: planoAcaoId,
      situacao_atual: situacaoAtual,
      impacto_observado: impactoObservado,
      causa_raiz_identificada: causaRaizIdentificada,
      escopo_plano: escopoPlano || null,
      areas_envolvidas: areasEnvolvidas.length > 0 ? areasEnvolvidas : null,
      porque_1: porques.porque_1 || null,
      porque_2: porques.porque_2 || null,
      porque_3: porques.porque_3 || null,
      porque_4: porques.porque_4 || null,
      porque_5: porques.porque_5 || null,
      elaborado_por: diagnostico?.elaborado_por || user?.id || null,
    });
  };

  const toggleArea = (area: string) => {
    if (readOnly) return;
    setAreasEnvolvidas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const score = calcularScoreGUT(gravidade, urgencia, tendencia);
  const prioridade = getGUTPrioridade(score);
  const isA3FormValid = situacaoAtual.trim() && impactoObservado.trim() && causaRaizIdentificada.trim();

  const renderDimensionButtons = (
    label: string,
    value: number,
    setValue: (v: number) => void,
    labels: Record<number, string>,
    justificativa: string,
    setJustificativa: (v: string) => void
  ) => (
    <div className="space-y-3">
      <Label className="text-base font-semibold">{label}</Label>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((v) => (
          <Button
            key={v}
            type="button"
            variant="outline"
            disabled={readOnly}
            className={cn(
              'flex flex-col h-auto py-3 px-2 transition-all',
              getValueColor(v),
              value === v && getSelectedColor(v)
            )}
            onClick={() => setValue(v)}
          >
            <span className="text-lg font-bold">{v}</span>
            <span className="text-[10px] leading-tight mt-1 text-center">
              {labels[v]}
            </span>
          </Button>
        ))}
      </div>
      <Textarea
        placeholder={`Justificativa para ${label.toLowerCase()} (opcional)`}
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        disabled={readOnly}
        className="text-sm resize-none"
        rows={2}
      />
    </div>
  );

  if (isLoadingGUT || isLoadingA3) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="gut" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Matriz GUT
            {avaliacao && <Badge variant="secondary" className="ml-1">{score}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="a3" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Diagnóstico A3
            {isDiagnosticoCompleto && (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gut" className="space-y-6 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Matriz GUT - Priorização</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {renderDimensionButtons(
                'GRAVIDADE',
                gravidade,
                setGravidade,
                GUT_LABELS.gravidade,
                gravidadeJustificativa,
                setGravidadeJustificativa
              )}
              {renderDimensionButtons(
                'URGÊNCIA',
                urgencia,
                setUrgencia,
                GUT_LABELS.urgencia,
                urgenciaJustificativa,
                setUrgenciaJustificativa
              )}
              {renderDimensionButtons(
                'TENDÊNCIA',
                tendencia,
                setTendencia,
                GUT_LABELS.tendencia,
                tendenciaJustificativa,
                setTendenciaJustificativa
              )}
            </CardContent>
          </Card>

          <GUTScoreDisplay
            gravidade={gravidade}
            urgencia={urgencia}
            tendencia={tendencia}
            score={score}
            prioridade={prioridade}
          />

          {!readOnly && (
            <div className="flex justify-end">
              <Button onClick={handleSaveGUT} disabled={isSavingGUT}>
                {isSavingGUT ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Avaliação GUT'
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="a3" className="space-y-6 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">1. Situação Atual *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Descreva detalhadamente o problema observado..."
                value={situacaoAtual}
                onChange={(e) => setSituacaoAtual(e.target.value)}
                disabled={readOnly}
                rows={4}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">2. Impacto Observado *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Quais são os efeitos negativos deste problema?"
                value={impactoObservado}
                onChange={(e) => setImpactoObservado(e.target.value)}
                disabled={readOnly}
                rows={4}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">3. Análise de Causa Raiz - 5 Porquês</CardTitle>
            </CardHeader>
            <CardContent>
              <CincoPorquesWizard
                valores={porques}
                onChange={setPorques}
                readOnly={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">4. Causa Raiz Identificada *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Qual é a causa raiz definitiva do problema?"
                value={causaRaizIdentificada}
                onChange={(e) => setCausaRaizIdentificada(e.target.value)}
                disabled={readOnly}
                rows={3}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">5. Escopo do Plano de Ação</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Delimite o que será tratado pelo plano de ação..."
                value={escopoPlano}
                onChange={(e) => setEscopoPlano(e.target.value)}
                disabled={readOnly}
                rows={3}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">6. Áreas Envolvidas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {AREAS_DISPONIVEIS.map((area) => (
                  <Badge
                    key={area}
                    variant={areasEnvolvidas.includes(area) ? 'default' : 'outline'}
                    className={cn(
                      'cursor-pointer transition-all',
                      areasEnvolvidas.includes(area) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted',
                      readOnly ? 'cursor-default' : ''
                    )}
                    onClick={() => toggleArea(area)}
                  >
                    {area}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {!readOnly && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                onClick={handleSaveA3}
                disabled={isSavingA3 || !isA3FormValid}
              >
                {isSavingA3 ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Diagnóstico A3'
                )}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
